import React from "react";
import Select, {
  StylesConfig,
  SingleValue,
  ActionMeta,
  GroupBase,
} from "react-select";

/**
 * Represents the structure of an option in the select dropdown
 * @typedef {Object} OptionType
 * @property {string} value - The value of the option
 * @property {string} label - The label of the option
 */
interface OptionType {
  value: string;
  label: string;
}

/**
 * Custom styles for the Select component
 * @type {StylesConfig<OptionType, false, GroupBase<OptionType>>}
 */
const customStylesWithFilterIcon: StylesConfig<
  OptionType,
  false,
  GroupBase<OptionType>
> = {
  control: (provided: any, state: any) => ({
    ...provided,
    borderColor: "#ced4da",
    borderRadius: "0.375rem",
    borderBottomLeftRadius: "0px",
    borderTopLeftRadius: "0px",
    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(153, 116, 4, 0.25)" : null,
    "&:hover": {
      borderColor: "#ccba82",
    },
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#B3812D" : "white",
    color: state.isFocused ? "white" : "grey",
    "&:active": {
      backgroundColor: "rgba(153, 116, 4, 0.25)",
    },
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "black",
  }),
  menu: (provided: any) => ({
    ...provided,
    marginTop: "0rem",
    paddingTop: "0rem",
    borderBottomLeftRadius: "5px",
    borderBottomRightRadius: "5px",
    borderTopRightRadius: "0px",
    borderTopLeftRadius: "0px",
    overflow: "hidden",
  }),
  menuList: (provided: any) => ({
    ...provided,
    borderBottomLeftRadius: "5px",
    borderBottomRightRadius: "5px",
    marginTop: "0rem",
    paddingTop: "0rem",
    marginBottom: "0rem",
    paddingBottom: "0rem",
    maxHeight: "200px",
  }),
};

const customStylesWithOutFilterIcon: StylesConfig<
  OptionType,
  false,
  GroupBase<OptionType>
> = {
  container: (provided: any) => ({
    ...provided,
    width: "100%",
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    borderColor: "#ced4da",
    borderRadius: "0.375rem",
    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(153, 116, 4, 0.25)" : null,
    "&:hover": {
      borderColor: "#ccba82",
    },
    width: "100%",
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#B3812D" : "white",
    color: state.isFocused ? "white" : "grey",
    "&:active": {
      backgroundColor: "rgba(153, 116, 4, 0.25)",
    },
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "black",
  }),
  menu: (provided: any) => ({
    ...provided,
    marginTop: "0rem",
    paddingTop: "0rem",
    borderBottomLeftRadius: "5px",
    borderBottomRightRadius: "5px",
    borderTopRightRadius: "0px",
    borderTopLeftRadius: "0px",
    overflow: "hidden",
  }),
  menuList: (provided: any) => ({
    ...provided,
    borderBottomLeftRadius: "5px",
    borderBottomRightRadius: "5px",
    marginTop: "0rem",
    paddingTop: "0rem",
    marginBottom: "0rem",
    paddingBottom: "0rem",
    maxHeight: "200px",
  }),
};

/**
 * Props for the CustomSelect component
 * @typedef {Object} CustomSelectProps
 * @property {OptionType[]} options - Array of options for the select dropdown
 * @property {OptionType | null} value - Currently selected value
 * @property {function} onChange - Callback function when selection changes
 */
interface CustomSelectProps {
  options: OptionType[];
  value: OptionType | null;
  onChange: (
    newValue: SingleValue<OptionType>,
    actionMeta: ActionMeta<OptionType>
  ) => void;
  placeholder?: string;
  isFilteredIconVisible?: boolean;
  customClass?: string;
  maxMenuHeight?: number;
  singleValueFontSize?: string; // Nueva propiedad para el tamaño del texto seleccionado
  optionFontSize?: string; // Nueva propiedad para el tamaño del texto de las opciones
}

/**
 * CustomSelect component
 * A reusable select component with custom styling
 * @param {CustomSelectProps} props - The props for the component
 * @returns {React.ReactElement} A styled Select component
 */
const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  isFilteredIconVisible,
  customClass,
  maxMenuHeight = 200, // Valor por defecto
  singleValueFontSize = "1rem", // Valor por defecto
  optionFontSize = "1rem", // Valor por defecto
}: CustomSelectProps): React.ReactElement => (
  <Select<OptionType>
    options={options}
    styles={
      isFilteredIconVisible
        ? {
            ...customStylesWithFilterIcon,
            menuList: (provided) => ({
              ...provided,
              maxHeight: `${maxMenuHeight}px`,
            }),
            singleValue: (provided) => ({
              ...provided,
              color: "black",
              fontSize: singleValueFontSize,
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isFocused ? "#B3812D" : "white",
              color: state.isFocused ? "white" : "grey",
              fontSize: optionFontSize,
              "&:active": {
                backgroundColor: "rgba(153, 116, 4, 0.25)",
              },
            }),
            placeholder: (provided) => ({
              ...provided,
              fontSize: singleValueFontSize, 
            }),
          }
        : {
            ...customStylesWithOutFilterIcon,
            menuList: (provided) => ({
              ...provided,
              maxHeight: `${maxMenuHeight}px`,
            }),
            singleValue: (provided) => ({
              ...provided,
              color: "black",
              fontSize: singleValueFontSize,
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isFocused ? "#B3812D" : "white",
              color: state.isFocused ? "white" : "grey",
              fontSize: optionFontSize,
              "&:active": {
                backgroundColor: "rgba(153, 116, 4, 0.25)",
              },
            }),
            placeholder: (provided) => ({
              ...provided,
              fontSize: singleValueFontSize, 
            }),
          }
    }
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className={`${customClass}`}
  />
);

export default CustomSelect;
